"use client";
import { Fragment, useEffect, useRef, useState } from "react";

import { Icon } from "@zapier/zinnia-icons";
import { Link, Spacer, Spinner, Text } from "@zapier/design-system";
import { useCanvas } from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { useEmitFolderDocumentationInteractionEvent } from "@zapier/shared";

import { CanvasEmbedZoomControls } from "../CanvasEmbedZoomControls";
import styles from "./CanvasEmbed.module.css";

interface CanvasEmbedProps {
  canvasId: string;
  name: string;
  folderId: string;
  isLoading?: boolean;
  autoLayoutOnLoad?: boolean;
  isGeneratingFromFolderAssets: boolean;
  onAutoLayoutConsumed?: () => void;
}

export function CanvasEmbed({
  canvasId,
  name,
  folderId,
  isLoading,
  autoLayoutOnLoad = false,
  isGeneratingFromFolderAssets,
  onAutoLayoutConsumed,
}: CanvasEmbedProps) {
  const t = useTranslations("folder-documentation");
  const { emit } = useEmitFolderDocumentationInteractionEvent();
  const iframeReference = useRef<HTMLIFrameElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const baseEmbedUrl = `/app/canvas/embed/${canvasId}`;
  const previousAutoLayoutOnLoadReference = useRef(autoLayoutOnLoad);

  const [iframeSource, setIframeSource] = useState<string>(() => {
    if (!autoLayoutOnLoad) return baseEmbedUrl;
    return `${baseEmbedUrl}?autolayout=1`;
  });
  const [isAutolayoutOverlayVisible, setIsAutolayoutOverlayVisible] =
    useState<boolean>(autoLayoutOnLoad);

  // Reset the iframe when we switch canvases. Do NOT tie this to `autoLayoutOnLoad`,
  // otherwise clearing the one-shot trigger (true -> false) would cause an iframe reload.
  useEffect(() => {
    previousAutoLayoutOnLoadReference.current = autoLayoutOnLoad;

    if (autoLayoutOnLoad) {
      setIframeSource(`${baseEmbedUrl}?autolayout=1`);
      setIsAutolayoutOverlayVisible(true);
      return;
    }

    setIframeSource(baseEmbedUrl);
    setIsAutolayoutOverlayVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasId]);

  // One-shot autolayout trigger: only change `src` when we transition false -> true.
  useEffect(() => {
    const previous = previousAutoLayoutOnLoadReference.current;
    previousAutoLayoutOnLoadReference.current = autoLayoutOnLoad;
    if (!autoLayoutOnLoad || previous) return;

    setIframeSource(`${baseEmbedUrl}?autolayout=1`);
    setIsAutolayoutOverlayVisible(true);
  }, [autoLayoutOnLoad, baseEmbedUrl]);

  useEffect(() => {
    const handleMessageFromCanvas = (event: MessageEvent) => {
      if (event.data?.type !== "canvasAutolayoutComplete") return;
      if (event.data?.canvasId !== canvasId) return;
      // The iframe removes the `autolayout` query param via history.replaceState (no reload).
      // Don't change `src` from the parent, or we'd force a second iframe navigation.
      setIsAutolayoutOverlayVisible(false);
      onAutoLayoutConsumed?.();
    };

    window.addEventListener("message", handleMessageFromCanvas);
    return () => window.removeEventListener("message", handleMessageFromCanvas);
  }, [canvasId, onAutoLayoutConsumed]);

  const { isError, isLoading: isCanvasLoading } = useCanvas(canvasId, {
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const isOverlayVisible =
    isAutolayoutOverlayVisible || isGeneratingFromFolderAssets;

  if (isLoading || isCanvasLoading) {
    return (
      <div className={styles.canvas_wrapper}>
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.canvas_wrapper}>
        <Icon name="actionLockFill" size={30} />
        <Spacer height={20} />
        <Text tag="p" type="ParagraphHeader2">
          {t("embed.noAccess")}
        </Text>
      </div>
    );
  }

  return (
    <div
      className={
        isExpanded
          ? `${styles.canvas_wrapper} ${styles.canvas_wrapper_expanded}`
          : styles.canvas_wrapper
      }
    >
      <Fragment>
        <CanvasEmbedZoomControls
          iframeRef={iframeReference}
          isExpanded={isExpanded}
          onToggleExpand={() => setIsExpanded(!isExpanded)}
        />
        {/* Keep keyboard tab focus on the surrounding controls (avoid tabbing into the embedded document). */}
        <iframe
          ref={iframeReference}
          src={iframeSource}
          title={name}
          className={styles.iframe}
          tabIndex={-1}
        />
        {isOverlayVisible && (
          <div className={styles.spinnerOverlay} aria-hidden>
            <Spinner />
          </div>
        )}
        <div className={styles.open_canvas_button}>
          <Link
            variant="primary"
            href={`/app/canvas/${canvasId}`}
            target="_blank"
            iconPosition="end"
            onClick={() =>
              emit({
                optionSelected: "open_canvas",
                folderId,
                canvasId,
              })
            }
          >
            {t("embed.open")}
            <Icon aria-hidden name="arrowOffsite" />
          </Link>
        </div>
      </Fragment>
    </div>
  );
}
